import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Avatar, AvatarImage } from '@codes/ui-kit/components/ui/avatar';
import { Badge } from '@codes/ui-kit/components/ui/badge';
import { CatalogImageSlider, Editor, SectionHeader, SettingsSection, useStereoIcon } from '@web/app/shared';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { createYooptaEditor } from '@yoopta/editor';
import { markdown } from '@yoopta/exports';
import { Box, ClipboardCheck, Layers, Monitor, Search, Server, Settings, Shield, Wrench } from 'lucide-react';

import { getCurrencySymbol } from '@codes/react-utils';
import { Button } from '@codes/ui-kit';
import { useWebCoreStore } from '@codes/web-core';

import advancedConsultingData from '../data/advanced-consulting.json';
import eurekakitData from '../data/eurekakit.json';
import { CatalogSetting, ShowCatalog } from '../types';

const catalogMap: Record<string, ShowCatalog> = {
    '387': eurekakitData as ShowCatalog,
    '500': advancedConsultingData as ShowCatalog,
};

const SETTING_SCOPES: { scope: CatalogSetting['scope']; icon: React.ElementType }[] = [
    { scope: 'consulting', icon: ClipboardCheck },
    { scope: 'infra', icon: Monitor },
    { scope: 'security', icon: Shield },
    { scope: 'devops', icon: Wrench },
    { scope: 'environ', icon: Server },
    { scope: 'config', icon: Settings },
    { scope: 'search', icon: Search },
];

export const ShowCatalogDetailPage = () => {
    const { t, i18n } = useTranslation();
    const { catalogId } = useParams();
    const navigate = useNavigate();
    const isAuthenticated = useWebCoreStore(state => state.isAuthenticated);
    const getStereoIcon = useStereoIcon();
    const yooptaEditor = useMemo(() => createYooptaEditor(), []);

    const handleAddService = () => {
        if (!isAuthenticated) {
            navigate('/auth/login');
            return;
        }
        navigate(`/`);
    };

    const catalog = catalogId ? catalogMap[catalogId] : undefined;

    useEffect(() => {
        if (!catalog) {
            return;
        }

        const content =
            i18n.language === 'en'
                ? catalog.readmeEn || catalog.readme || ''
                : catalog.readme || catalog.readmeEn || '';

        if (!content) {
            return;
        }

        const value = markdown.deserialize(yooptaEditor, content);
        yooptaEditor.setEditorValue(value);
    }, [catalog, i18n.language, yooptaEditor]);

    if (!catalog) {
        return (
            <div className="h-[60vh] flex flex-col justify-center items-center gap-3">
                <Box className="w-12 h-12 text-placeholder" />
                <div className="text-sub-title text-lg">{t('common.notFound', 'Not Found')}</div>
            </div>
        );
    }

    const localizedName = (i18n.language === 'en' && catalog.nameEn) || catalog.name;
    const localizedSummary = (i18n.language === 'en' && catalog.summaryEn) || catalog.summary;
    const localizedDesc = (i18n.language === 'en' && catalog.descEn) || catalog.desc;
    const hasReadme = !!(catalog.readme || catalog.readmeEn);
    const hasSettings = catalog.setting$$ && catalog.setting$$.length > 0;
    const currencySymbol = getCurrencySymbol(catalog.price$.currency);
    const isConsulting = catalog.setting$$?.some(s => s.scope === 'consulting');

    return (
        <PageContainer maxWidth="lg" className="lg:px-12 pb-24">
            {/* Hero: 2-column on desktop */}
            <div className="pt-12 pb-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 items-start">
                {/* Left: Image */}
                <div>
                    <CatalogImageSlider
                        images={catalog.images}
                        name={catalog.name}
                        stereo={catalog.stereo}
                        iconImage={getStereoIcon(catalog.stereo)}
                    />
                </div>

                {/* Right: Info + Price */}
                <div className="flex flex-col gap-6">
                    {/* Product Info */}
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <Avatar className="w-12 h-12 border-2 border-point/20 shadow-sm">
                                <AvatarImage src={getStereoIcon(catalog.stereo)} alt={catalog.stereo} />
                            </Avatar>
                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h1 className="text-2xl font-bold text-title tracking-tight">{localizedName}</h1>
                                    <Badge className="text-[11px] h-5 px-2">
                                        {t(catalog.isDomestic ? 'serviceDetail.domestic' : 'serviceDetail.global')}
                                    </Badge>
                                </div>
                                <p className="text-sm text-sub-title">by {catalog.owner$.name}</p>
                            </div>
                        </div>
                        <p className="text-sm text-sub leading-relaxed">{localizedSummary}</p>
                    </div>

                    {/* Price Card */}
                    {catalog.price$ && (
                        <div className="rounded-2xl bg-point/5 border border-point/15 p-5">
                            <div className="text-xs font-medium text-point uppercase tracking-wider mb-1">
                                {isConsulting
                                    ? t('price.service.one_time_fee', '1회 비용')
                                    : t('price.service.monthly_fee', 'Monthly Fee')}
                            </div>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span className="text-3xl font-bold text-title tracking-tight">
                                    {currencySymbol}
                                    {catalog.price$.initPrice.toLocaleString()}
                                </span>
                                <span className="text-xs text-sub">({t('price.vat_excluded', 'VAT 별도')})</span>
                            </div>
                            <div className="space-y-1.5 border-t border-point/10 pt-3">
                                <div className="flex justify-between text-sm">
                                    <span className="text-sub-title">{t('price.service.traffic_cost', 'Traffic')}</span>
                                    <span className="text-sub font-medium">
                                        {currencySymbol}
                                        {catalog.price$.trafficPrice.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-sub-title">{t('price.service.cost_per_hour', 'Hourly')}</span>
                                    <span className="text-sub font-medium">
                                        {currencySymbol}
                                        {catalog.price$.hourPerPrice.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-sub-title">{t('price.service.per_call', 'Per Call')}</span>
                                    <span className="text-sub font-medium">
                                        {currencySymbol}
                                        {catalog.price$.callPerPrice.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-divider mb-10" />

            {/* Overview */}
            {localizedDesc && (
                <section className="mb-10">
                    <SectionHeader icon={Layers} title={t('serviceDetail.overview')} />
                    <p className="text-sub leading-relaxed max-w-3xl">{localizedDesc}</p>
                </section>
            )}

            {/* README */}
            {hasReadme && (
                <section className="mb-10">
                    <SectionHeader icon={Box} title="README" />
                    <div className="rounded-xl border border-detail-line bg-card p-6">
                        <Editor editor={yooptaEditor} readOnly={true} />
                    </div>
                </section>
            )}

            {/* Settings */}
            {hasSettings && (
                <section className="mb-10">
                    <SectionHeader icon={Settings} title="Settings" />
                    {SETTING_SCOPES.map(({ scope, icon }) => (
                        <SettingsSection key={scope} settings={catalog.setting$$} scope={scope} icon={icon} />
                    ))}
                </section>
            )}

            {/* Service Guidelines */}
            <section className="mb-10">
                <div className="rounded-xl bg-box border border-detail-line p-4">
                    <div className="text-xs font-medium text-sub-title mb-2">
                        {t('serviceDetail.serviceGuidelines')}
                    </div>
                    <ul className="flex flex-col gap-1.5">
                        {[t('guide.callbackNotice'), t('guide.approvalNotice'), t('guide.cancellationNotice')].map(
                            (content, index) => (
                                <li
                                    key={index}
                                    className="flex items-start gap-2 text-[11px] text-placeholder leading-relaxed"
                                >
                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-outline-text shrink-0" />
                                    {content}
                                </li>
                            )
                        )}
                    </ul>
                </div>
            </section>

            {/* Fixed bottom button */}
            <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-divider p-4 z-50">
                <div className="max-w-6xl mx-auto px-8 lg:px-12">
                    <div className="max-w-[500px] mx-auto">
                        <Button variant="default" size="lg" className="w-full" onClick={handleAddService}>
                            {t('project.addService')}
                        </Button>
                    </div>
                </div>
            </div>
        </PageContainer>
    );
};
